/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC } from "react";
import { Link, useLocation } from "react-router-dom";

import LazyLoadImage from "web/Layout/Common/LazyLoadImage";
import Price from "web/Layout/Common/Price";
import __ from "web/Layout/Translations";

import useOnProductClikCallback from "web/hooks/useOnProductClikCallback";

import formatNumber from "web/utils/data/parser/number/formatNumber";
import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";

import { PropsWithClasses } from "web/types/Common";
import { IProduct } from "web/types/Product";

import classify from "web/classify";

import { ProductCardCarouselProps } from "./container";
import defaultClasses from "./product.scss";

type ProductCardCarouselProductProps = ProductCardCarouselProps;

const ProductCardCarouselProduct: FC<
  PropsWithClasses<ProductCardCarouselProductProps>
> = ({
  classes = {},
  product,
  gtmProduct,
  positionNumber,
  carouselLength,
  listingAmount,
  listingPosition,
  listingCategory = "",
}) => {
  if (!product) {
    return null;
  }
  // @ts-ignore
  const { url, name, image, price, finalPrice, currency, options } = product;
  const { search } = useLocation();

  const productTyped = gtmProduct as IProduct<unknown>;
  const [sendProductClickEvent] = useOnProductClikCallback(
    productTyped,
    positionNumber,
    "",
    listingPosition,
    listingAmount,
    listingCategory
  );

  const isConfigurable = isArrayHasItems(
    options?.configurable_options?.attributes
  );

  const convertName = () => {
    if (!name) {
      return "";
    }
    const splittedName = __(name).split(" ");
    const convertedName = splittedName.map((item) => {
      if (item.length > 20 && item.includes(".")) {
        return item.replace(".", " .");
      }
      return item;
    });

    return convertedName.toString().replaceAll(",", " ");
  };

  const setPositionNumber = () => {
    const newParams = new URLSearchParams(search);

    newParams.set("position_id", positionNumber?.toString() || "");
    newParams.set("listing_amount", listingAmount?.toString() || "");
    newParams.set("listing_position", listingPosition?.toString() || "");
    newParams.set("listing_category", listingCategory?.toString() || "");

    return newParams.toString();
  };

  const urlSearchParams = setPositionNumber();
  const redirectTo = {
    pathname: url,
    search: urlSearchParams,
  };

  const priceClassName =
    finalPrice < price ? classes.priceDiscount : classes.price;
  const displayName = convertName();
  const accessiblePrice = price
    ? [
        isConfigurable ? __("od") : null,
        formatNumber(finalPrice as string),
        currency || null,
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  const accessibleDetails = [
    accessiblePrice ? `, ${accessiblePrice}` : "",
    ` - ${__("strona produktu")}`,
    positionNumber && carouselLength
      ? `, ${__("element")} ${positionNumber} ${__("z")} ${carouselLength}`
      : "",
  ].join("");

  return (
    <article className={classes.root}>
      <Link
        to={redirectTo}
        className={classes.link}
        onClick={sendProductClickEvent}
      >
        <div className={classes.box}>
          <div className={classes.imgLink}>
            <LazyLoadImage
              src={image}
              alt=""
              className={classes.img}
              isProduct
              width={280}
              height={187}
            />
          </div>
          {!!price && (
            <div aria-hidden="true">
              <Price
                className={priceClassName}
                currency={currency}
                value={finalPrice}
                from={isConfigurable}
              />
            </div>
          )}
        </div>
        <h3 className={classes.title}>
          {displayName}
          {accessibleDetails ? (
            <span className="visually-hidden">{accessibleDetails}</span>
          ) : null}
        </h3>
      </Link>
    </article>
  );
};

export default classify<PropsWithClasses<ProductCardCarouselProductProps>>(
  defaultClasses
)(ProductCardCarouselProduct);
